import { useEffect, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, X, CheckCircle2, AlertTriangle, Sparkles, CalendarPlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HistoryUpload } from "@/components/HistoryUpload";
import { api, errorText } from "@/lib/api";
import { MONTHS } from "@/lib/format";
import { toast } from "sonner";

export const UploadStep = ({ project, onUploaded, onNext, onHistoryChanged, disabled = false }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();
  const summary = project.dataset?.summary;
  const [mode, setMode] = useState(project.dataset ? "append" : "replace");
  useEffect(() => { if (project.dataset) setMode("append"); }, [project.dataset?.last_actual_month, project.dataset?.source_files?.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [stored, setStored] = useState([]);
  const loadStored = () => api.get(`/projects/${project.id}/upload/files`).then((r) => setStored(r.data)).catch(() => setStored([]));
  useEffect(() => { loadStored(); }, [project.id, project.dataset?.last_actual_month]); // eslint-disable-line react-hooks/exhaustive-deps
  const removeStored = async (f) => {
    if (!window.confirm(`Remove ${f.name} and rebuild the actuals from the remaining reports?`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/projects/${project.id}/upload/files/${f.id}`);
      if (data.remaining > 0) {
        let job = { status: "running" };
        while (job.status === "running") { await new Promise((r) => setTimeout(r, 3000)); job = (await api.get(`/projects/${project.id}/upload/status`)).data; setJobMsg(job.message || ""); }
        if (job.status !== "done") throw new Error(job.message || "Rebuild failed");
        onUploaded(job.result);
      }
      toast.success(`${f.name} removed`); loadStored();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); setJobMsg(""); }
  };

  const addFiles = (list) => {
    const arr = Array.from(list).filter((f) => /\.(xlsx|xls|xlsm|csv)$/i.test(f.name));
    if (arr.length !== list.length) toast.error("Only .xlsx, .xls or .csv files are supported");
    setFiles((prev) => [...prev, ...arr.filter((f) => !prev.some((p) => p.name === f.name))]);
  };

  const [jobMsg, setJobMsg] = useState("");
  const upload = async () => {
    if (!files.length) return;
    if (project.dataset && mode === "replace" && stored.length && !window.confirm(`Replace all: the ${stored.length} report(s) already on file (${stored.map((f) => f.period_month ? MONTHS[f.period_month - 1] : f.name).join(", ")}) will be removed and only the new file(s) kept — months without a report become an even spread of the YTD figure. Use "Add months (merge)" to keep them. Continue?`)) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (project.dataset && mode === "append") fd.append("append", "1");
    if (project.dataset && mode === "replace") fd.append("replace", "1");
    try {
      await api.post(`/projects/${project.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      let job = { status: "running" };
      while (job.status === "running") {
        await new Promise((r) => setTimeout(r, 3000));
        job = (await api.get(`/projects/${project.id}/upload/status`)).data;
        setJobMsg(job.message || "");
      }
      if (job.status !== "done") throw new Error(job.message || "Upload failed");
      const months = job.result?.months_with_data || [];
      toast.success(`${job.message || "Files processed"}${months.length ? ` — reported months on file: ${months.map((m) => MONTHS[m - 1]).join(", ")}` : ""}`, { duration: 8000 });
      setFiles([]);
      loadStored();
      onUploaded(job.result);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
      setJobMsg("");
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-6">
      <div className="space-y-5">
        {project.dataset && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-xs flex flex-wrap items-center gap-3" data-testid="upload-mode-bar">
            <CalendarPlus size={15} className="text-emerald-700 shrink-0" />
            <div className="flex-1 min-w-[220px]">
              <p className="font-semibold text-emerald-900">Add monthly reports to the existing actuals</p>
              <p className="text-emerald-800/80">Upload the month-by-month P&amp;L reports (e.g. January … July) — each report fills its own month with real figures and replaces the YTD distribution; the budget is regenerated with your current assumptions and manual overrides are kept.</p>
            </div>
            <div className="flex rounded-md border border-emerald-300 overflow-hidden text-[11px]" role="radiogroup">
              <button type="button" onClick={() => setMode("append")} className={`px-2.5 py-1.5 ${mode === "append" ? "bg-emerald-700 text-white" : "bg-white text-slate-600"}`} data-testid="upload-mode-append">Add months (merge)</button>
              <button type="button" onClick={() => setMode("replace")} className={`px-2.5 py-1.5 ${mode === "replace" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="upload-mode-replace">Replace all</button>
            </div>
          </div>
        )}
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          onClick={() => !disabled && inputRef.current?.click()}
          data-testid="file-drop-zone"
          className={`rounded-lg border-2 border-dashed p-10 sm:p-14 text-center transition-colors bg-white ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${drag ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-emerald-400"}`}
        >
          <input ref={inputRef} type="file" multiple accept=".xlsx,.xls,.xlsm,.csv" className="hidden" data-testid="file-input" onChange={(e) => addFiles(e.target.files)} />
          <UploadCloud className="mx-auto text-emerald-600" size={36} />
          <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mt-4">Drop the actual P&amp;L files here</h3>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Upload monthly Profit &amp; Loss reports (Current Month / Year to Date layout) or files with Jan–Dec month columns. Several files at once are supported (e.g. Jan through August).
          </p>
          <p className="text-xs text-slate-400 mt-3">.xlsx · .xls · .csv — any PMS export (VHP, Opera, Power Pro, custom) — column structure and departments are detected automatically. The month is read from the report header or from the file name (e.g. "SAMA Januari 2026.xls", "Sep.xlsx").</p>
        </div>

        {files.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="selected-files-list">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Selected files ({files.length})</p>
            <ul className="space-y-2">
              {files.map((f) => (
                <li key={f.name} className="flex items-center gap-3 text-sm">
                  <FileSpreadsheet size={16} className="text-emerald-600 shrink-0" />
                  <span className="truncate flex-1 text-slate-800">{f.name}</span>
                  <span className="text-xs text-slate-400 font-mono">{(f.size / 1024).toFixed(0)} KB</span>
                  <button onClick={() => setFiles(files.filter((x) => x !== f))} className="text-slate-400 hover:text-red-600" data-testid={`remove-file-${f.name}`}><X size={14} /></button>
                </li>
              ))}
            </ul>
            <Button onClick={upload} disabled={busy} className="mt-4 w-full bg-slate-900 hover:bg-emerald-700" data-testid="upload-submit-button">
              {busy ? (<><Sparkles size={15} className="mr-2 animate-pulse" /> {jobMsg || "Reading the report structure & classifying USALI accounts..."}</>) : "Process Files"}
            </Button>
            {busy && <p className="text-[11px] text-slate-500 mt-2" data-testid="upload-job-message">Large reports (many department sheets) can take a minute or two. Any property management system layout is supported — sheets that are not departmental P&amp;Ls (balance sheet, cash flow, ledgers) are skipped automatically.</p>}
          </div>
        )}
      </div>

      <aside className="space-y-4">
        {stored.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="stored-files-list">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reports on file ({stored.length})</p>
            <ul className="mt-3 space-y-1.5">
              {stored.map((f) => (
                <li key={f.id} className="flex items-center gap-2 text-xs" data-testid={`stored-file-${f.id}`}>
                  <FileSpreadsheet size={13} className="text-emerald-600 shrink-0" />
                  <span className="truncate flex-1 text-slate-800" title={f.name}>{f.name}</span>
                  <span className="font-mono text-slate-500 shrink-0">{f.period_month ? MONTHS[f.period_month - 1].slice(0, 3) : "Jan–Dec"}</span>
                  {!disabled && <button type="button" onClick={() => removeStored(f)} disabled={busy} className="text-slate-400 hover:text-red-600" title="Remove this report and rebuild" data-testid={`stored-file-remove-${f.id}`}><Trash2 size={13} /></button>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="upload-preview-table">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Detected Structure</p>
          {!summary ? (
            <p className="text-sm text-slate-500 mt-3">No data yet. Upload files to see sheets, line items and the last actual month.</p>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-md bg-slate-50 p-3">
                  <p className="text-[11px] text-slate-500">Actual year</p>
                  <p className="font-mono font-semibold text-slate-900" data-testid="detected-actual-year">{project.dataset.actual_year}</p>
                </div>
                <div className="rounded-md bg-emerald-50 p-3">
                  <p className="text-[11px] text-emerald-700">Actuals through</p>
                  <p className="font-mono font-semibold text-emerald-900" data-testid="detected-last-month">{project.dataset.last_actual_month ? MONTHS[project.dataset.last_actual_month - 1] : "-"}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">Months with data: <span className="font-mono text-slate-800">{(project.dataset.months_with_data || []).map((m) => MONTHS[m - 1]).join(", ") || "-"}</span>. Following months will be forecast.</p>
              <ul className="mt-4 space-y-2" data-testid="detected-sheets-list">
                {summary.sheets.map((s) => (
                  <li key={s.name} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-slate-900 font-medium truncate">{s.name} <span className="text-xs text-slate-400 font-normal">· {s.department}</span></p>
                      <p className="text-xs text-slate-500">{s.lines} line items · {s.formulas} total/ratio formulas · layout {s.layout}{s.ai_layout ? " (AI)" : ""}</p>
                    </div>
                  </li>
                ))}
                {summary.skipped.map((s) => (
                  <li key={s.sheet} className="flex items-start gap-2 text-sm">
                    <AlertTriangle size={15} className="text-amber-500 mt-0.5 shrink-0" />
                    <div><p className="text-slate-700">{s.sheet}</p><p className="text-xs text-slate-500">{s.reason}</p></div>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-400 mt-4">Files: {(project.dataset.source_files || []).map((f) => f.name).join(", ")}</p>
            </>
          )}
        </div>
        {project.budget_source !== "file" && <HistoryUpload project={project} onChanged={onHistoryChanged} disabled={disabled} />}
        {summary && (
          <Button onClick={onNext} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="go-to-assumptions-button">{project.budget_source === "file" ? "View Budget vs Actual results →" : "Continue to Assumptions →"}</Button>
        )}
      </aside>
    </div>
  );
};
